import { useContext, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../context/auth-context';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Drawer,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PaidIcon from '@mui/icons-material/Paid';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import Sidebar from '../components/Sidebar';
import { getPayrollRecords, fetchUsers } from '../api/services';

const FinancialAdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [totalElements, setTotalElements] = useState(0);
  const [earningsData, setEarningsData] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState(0);

  useEffect(() => {
    setRowsPerPage(isMobile ? 5 : 10);
  }, [isMobile]);

  const fetchDashboardData = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        const usersResponse = await fetchUsers(0, 100);
        let users = [];
        if (Array.isArray(usersResponse)) {
          users = usersResponse;
        } else if (usersResponse && usersResponse.content) {
          users = usersResponse.content;
        }
        setTotalEmployees(users.length);
      } catch (error) {
        console.error('Error fetching users:', error);
        setTotalEmployees(0);
      }

      const response = await getPayrollRecords(page, rowsPerPage);
      
      let records = [];
      let total = 0;
      
      if (response && response.content && Array.isArray(response.content)) {
        records = response.content;
        total = response.totalElements || records.length;
      } else if (Array.isArray(response)) {
        records = response;
        total = records.length;
      } else {
        records = [];
        total = 0;
      }

      const mapped = records.map((r) => ({
        id: r.id,
        userId: r.bankAccount?.userId ?? 'N/A',
        name: r.bankAccount?.fullName ?? 'Unknown',
        bankAccount: r.bankAccount
          ? `${r.bankAccount.bankName} (${r.bankAccount.accountNumber})`
          : 'No Bank Account',
        bankAccountId: r.bankAccount?.id ?? null,
        grossPay: r.grossPay || 0,
        bonus: r.bonus || 0,
        tax: r.tax ?? 0,
        withholdingTax: r.withholdingTax ?? 0,
        housingLevy: r.housingLevy ?? 0,
        sha: r.sha ?? 0,
        nssf: r.nssf ?? 0,
        paymentDate: r.currentPaymentDate,
        paymentStatus: r.paymentStatus || 'No',
        userType: r.userType ?? 'Employee',
      }));

      setEarningsData(mapped);
      setTotalElements(total);

      const currentPaymentDate = records.length > 0
        ? records[0].currentPaymentDate || 'N/A'
        : 'N/A';

      const totalPayroll = records.reduce((sum, r) => sum + (parseFloat(r.grossPay) || 0), 0);
      const processedPayments = records.filter(r => r.paymentStatus === 'Yes').length;

      setDashboardData({
        currentPayment: currentPaymentDate,
        totalPayroll: totalPayroll,
        processedPayments: processedPayments,
        totalRecords: records.length,
      });

      if (showRefresh) {
        setSnackbarMessage('Dashboard refreshed successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage(
        error.response?.status === 404
          ? 'No payroll records found.'
          : error.message || 'Error fetching dashboard data'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDashboardData({
        currentPayment: 'N/A',
        totalPayroll: 0,
        processedPayments: 0,
        totalRecords: 0,
      });
      setEarningsData([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
  };

  const statsCards = [
    {
      title: 'Total Employees',
      value: totalEmployees || 0,
      color: '#009db9',
    },
    {
      title: 'Total Payroll',
      value: dashboardData?.totalPayroll ? `KSh ${dashboardData.totalPayroll.toLocaleString()}` : 'KSh 0',
      color: '#2e7d32',
    },
    {
      title: 'Processed Payments',
      value: dashboardData?.processedPayments || 0,
      color: '#1976d2',
    },
  ];

  const getStatusChip = (status) => {
    if (status === 'Yes') {
      return <Chip label="Processed" color="success" size="small" />;
    } else if (status === 'No') {
      return <Chip label="Pending" color="warning" size="small" />;
    }
    return <Chip label={status || 'N/A'} color="default" size="small" />;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            pt: { xs: '10px', md: '120px' },
            bgcolor: '#f8fafc',
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, md: 3 },
            position: 'relative',
          }}
        >
          {/* Mobile Menu Button */}
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'sticky',
              top: { xs: '64px', sm: '80px' },
              zIndex: 1100,
              mb: 2,
            }}
          >
            <Tooltip title="Menu" arrow>
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  background: 'linear-gradient(135deg, #009db9 0%, #00707f 100%)',
                  color: '#fff',
                  borderRadius: '12px',
                  p: 1.2,
                  boxShadow: '0 4px 12px rgba(0,157,185,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00707f 0%, #005a66 100%)',
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 16px rgba(0,157,185,0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
                aria-label="toggle sidebar"
              >
                <MenuIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem' } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Welcome Card */}
          <Card
            sx={{
              mb: 4,
              background: 'linear-gradient(135deg, #009db9 0%, #00707f 50%, #005a66 100%)',
              color: '#fff',
              borderRadius: '24px',
              px: { xs: 3, md: 5 },
              py: { xs: 4, md: 5 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 12px 40px rgba(0,157,185,0.3)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1, flex: 1, width: '100%' }}>
              <Typography 
                variant="overline" 
                sx={{ 
                  opacity: 0.8, 
                  letterSpacing: 2, 
                  fontSize: { xs: '0.65rem', sm: '0.75rem' } 
                }}
              >
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </Typography>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                mb: 1, 
                fontSize: { xs: '1.5rem', md: '2.125rem' }
              }}>
                {getGreeting()}, {user?.name || 'Financial Admin'}!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Here's where things stand
              </Typography>
            </Box>
            <Box sx={{ 
              textAlign: { xs: 'left', md: 'right' },
              position: 'relative',
              zIndex: 1,
              mt: { xs: 2, md: 0 },
              width: { xs: '100%', md: 'auto' }
            }}>
              <Typography sx={{ 
                color: 'rgba(255,255,255,0.75)', 
                fontSize: '0.8rem', 
                fontWeight: 'bold', 
                textTransform: 'uppercase', 
                letterSpacing: '0.04em', 
                mb: 0.5 
              }}>
                Current Payment
              </Typography>
              {loading ? (
                <CircularProgress size={28} sx={{ color: '#fff' }} />
              ) : (
                <Typography sx={{ 
                  color: '#fff', 
                  fontWeight: 'bold', 
                  fontSize: { xs: '1.5rem', md: '2.1rem' } 
                }}>
                  {dashboardData?.currentPayment || 'N/A'}
                </Typography>
              )}
            </Box>
          </Card>

          {/* Quick Stats - No Icons */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              {statsCards.map((stat, index) => (
                <Grid item xs={6} md={4} key={index}>
                  <Card
                    sx={{
                      borderRadius: '16px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      borderLeft: `4px solid ${stat.color}`,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box>
                        <Typography sx={{ 
                          color: '#64748b', 
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          mb: 0.5
                        }}>
                          {stat.title}
                        </Typography>
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: stat.color }} />
                        ) : (
                          <Typography sx={{ 
                            fontWeight: 700, 
                            color: '#0f172a',
                            fontSize: { xs: '1.3rem', md: '2rem' }
                          }}>
                            {stat.value}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Recent Payroll Records Table */}
          <Card
            sx={{
              borderRadius: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  mb: 3,
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#333', 
                    letterSpacing: 1,
                    fontSize: { xs: '1.1rem', md: '1.5rem' }
                  }}
                >
                  Recent Payroll Records
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                  <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={20} sx={{ color: '#009db9' }} /> : <RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refreshing || loading}
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      borderColor: '#e0e0e0',
                      color: '#333',
                      fontSize: { xs: '0.7rem', md: '0.875rem' },
                      '&:hover': {
                        borderColor: '#009db9',
                        color: '#009db9',
                      },
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/earnings-management"
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      backgroundColor: '#009db9',
                      color: '#fff',
                      fontSize: { xs: '0.7rem', md: '0.875rem' },
                      '&:hover': { backgroundColor: '#008ba3' },
                    }}
                  >
                    View All
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ mb: 4 }} />

              {loading ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <CircularProgress sx={{ color: '#009db9' }} />
                  <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
                    Loading payroll records...
                  </Typography>
                </Box>
              ) : earningsData.length === 0 ? (
                <Typography variant="body1" sx={{ textAlign: 'center', color: '#333', py: 4 }}>
                  No payroll records found.
                </Typography>
              ) : (
                <>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 650, border: '1px solid #e0e0e0' }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#009db9' }}>
                          {['Name', 'Payment Method', 'Gross Pay', 'Payment Date', 'Status'].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 'bold',
                                color: '#fff',
                                py: 2,
                                px: 1,
                                borderBottom: '2px solid #e0e0e0',
                                whiteSpace: 'nowrap',
                                fontSize: { xs: '0.6rem', md: '0.85rem' },
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {earningsData.map((e) => (
                          <TableRow key={e.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                            <TableCell sx={{ py: 2, px: 1, fontSize: { xs: '0.6rem', md: '0.85rem' } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#e0f7fa', color: '#009db9' }}>
                                  {e.name?.[0] || 'E'}
                                </Avatar>
                                {e.name}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 2, px: 1, fontSize: { xs: '0.6rem', md: '0.85rem' } }}>
                              {isMobile ? e.bankAccount.substring(0, 15) + '...' : e.bankAccount}
                            </TableCell>
                            <TableCell sx={{ py: 2, px: 1, fontWeight: 600, fontSize: { xs: '0.6rem', md: '0.85rem' } }}>
                              KSh {(e.grossPay || 0).toLocaleString()}
                            </TableCell>
                            <TableCell sx={{ py: 2, px: 1, fontSize: { xs: '0.6rem', md: '0.85rem' } }}>
                              {e.paymentDate || 'N/A'}
                            </TableCell>
                            <TableCell sx={{ py: 2, px: 1 }}>
                              {getStatusChip(e.paymentStatus)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25, 50, 100]}
                    component="div"
                    count={totalElements}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ 
                      mt: 2, 
                      color: '#333',
                      '.MuiTablePagination-selectLabel': {
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                      },
                      '.MuiTablePagination-displayedRows': {
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                      },
                    }}
                    labelRowsPerPage="Rows per page:"
                    showFirstButton
                    showLastButton
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: 1100,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            top: { xs: '64px', sm: '80px' },
            height: 'calc(100% - 64px)',
            borderRight: 'none',
            boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: '12px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialAdminDashboard;